// Angular
import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Third party
import * as _ from 'lodash';
import moment from 'moment';

// Local library
import { IDataProduct } from '@amwager/util-ts';

// Local file
import { WagerFacade } from '../+state/wager.facade';
import { IDataPrograms } from '../+state/wager.models';

/**
 * Track entry backing the Program page track filter
 */
export interface IProgramTrack {
  id: string;
  name: string;
}

/**
 * Normalized DataProducts payload — sorted products plus their tracks
 */
export interface IProgramsNormalized {
  products: IDataProduct[];
  tracks: IProgramTrack[];
}

/** Date format the DataProducts endpoint expects */
const API_DATE_FORMAT = 'MM-DD-YYYY 00:00:00';

/** Prefix a base64 payload carries when it arrives as a data URI */
const BASE64_MARKER = ';base64,';

/**
 * Data helpers for the Program page. Owns form scaffolding, the
 * DataProducts dispatches, list normalization and the base64 to blob
 * conversion so the component stays thin.
 * Port of frontend/js/program-shop.js.
 */
@Injectable({ providedIn: 'root' })
export class ProgramService {

  private readonly formBuilder = inject(FormBuilder);
  private readonly wagerFacade = inject(WagerFacade);

  /**
   * Build the reactive form used by the Program view. `date` is kept
   * as a real `Date` so the mat-datepicker can round-trip it through
   * its `DateAdapter`.
   * @returns {FormGroup} form with date and eventId
   */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      date: [new Date(), [Validators.required]],
      eventId: ['']
    });
  }

  /**
   * Fetch the programs on sale for a date, unless the store already
   * holds that date's list or is fetching it
   * @param {string} sourceInitiator Caller, for action tracing
   * @param {Date | string} date Race date
   * @param {IDataPrograms} cached Store slice
   * @returns {void}
   */
  refreshPrograms(
    sourceInitiator: string,
    date: Date | string,
    cached?: IDataPrograms
  ): void {
    const apiDate = this.toApiDate(date);

    // Already fetching or already resolved for this date
    if (cached?.date === apiDate && (cached?.isProcessing || cached?.isSuccess !== undefined)) {
      return;
    }

    this.wagerFacade.getDataPrograms(sourceInitiator, apiDate);
  }

  /**
   * Download a product, purchasing it when it is not already owned
   * @param {string} sourceInitiator Caller, for action tracing
   * @param {IDataProduct} product Product to download
   * @returns {void}
   */
  downloadProgram(sourceInitiator: string, product: IDataProduct): void {
    this.wagerFacade.downloadDataProgram(
      sourceInitiator,
      String(product?.RaceDate ?? ''),
      String(product?.ID ?? '')
    );
  }

  /**
   * Drop the program PDF held in the store
   * @param {string} sourceInitiator Caller, for action tracing
   * @returns {void}
   */
  clearDownload(sourceInitiator: string): void {
    this.wagerFacade.clearDataProgramDownload(sourceInitiator);
  }

  /**
   * Drop products the API returns without a track, then sort them by
   * track and product name and derive the unique track list
   * @param {IDataProduct[]} products Raw DataProducts response
   * @returns {IProgramsNormalized} Sorted products and their tracks
   */
  normalizePrograms(products?: IDataProduct[] | null): IProgramsNormalized {
    const named = _.filter(products || [], (product) => !!product?.EventName);

    const sorted = _.orderBy(
      named,
      [
        (product) => (product?.EventName || '').toLowerCase(),
        (product) => (product?.ProdName || '').toLowerCase()
      ],
      ['asc', 'asc']
    );

    const tracks = _.uniqBy(
      _.map(sorted, (product) => ({
        id: String(product?.EventId ?? ''),
        name: String(product?.EventName ?? '')
      })),
      'id'
    );

    return { products: sorted, tracks };
  }

  /**
   * Narrow the product list to a single track
   * @param {IDataProduct[]} products Source products
   * @param {string} eventId Track event id, empty for all tracks
   * @returns {IDataProduct[]} Filtered products
   */
  filterByTrack(products: IDataProduct[], eventId: string): IDataProduct[] {
    if (!eventId) return products;

    return _.filter(products, (product) => String(product?.EventId) === eventId);
  }

  /**
   * Match a track by id, falling back to a case-insensitive name match
   * so a hand-off from the wager page still lands on the right track
   * @param {IProgramTrack[]} tracks Available tracks
   * @param {string} eventId Event id to look for
   * @param {string} eventName Event name to look for
   * @returns {string} Matching track id, or an empty string
   */
  resolveTrackId(tracks: IProgramTrack[], eventId?: string, eventName?: string): string {
    if (eventId && _.some(tracks, { id: String(eventId) })) return String(eventId);

    const name = (eventName || '').toLowerCase();
    const byName = name
      ? _.find(tracks, (track) => track.name.toLowerCase() === name)
      : undefined;

    return byName?.id ?? '';
  }

  /**
   * Turn a base64 PDF payload into an object URL for the viewer.
   * The caller owns revoking it.
   * @param {string} base64 Base64-encoded PDF
   * @returns {string} Object URL, or an empty string when undecodable
   */
  toPdfObjectUrl(base64: string): string {
    const bytes = this.toBinary(base64);
    if (!bytes) return '';

    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  }

  /**
   * Format a date the way the DataProducts endpoint expects
   * @param {Date | string} date Race date
   * @returns {string} Date as `MM-DD-YYYY 00:00:00`
   */
  toApiDate(date: Date | string): string {
    return moment(date).format(API_DATE_FORMAT);
  }

  /**
   * Decode a base64 PDF payload, tolerating a data-URI prefix and the
   * line breaks some responses wrap the payload in
   * @param {string} base64 Base64-encoded PDF
   * @returns {Uint8Array | null} Decoded bytes, or null when undecodable
   */
  private toBinary(base64: string): Uint8Array | null {
    if (!base64) return null;

    const marker = base64.indexOf(BASE64_MARKER);
    const payload = (marker === -1 ? base64 : base64.slice(marker + BASE64_MARKER.length))
      .replace(/\s/g, '');

    try {
      const raw = window.atob(payload);
      const bytes = new Uint8Array(raw.length);

      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);

      return bytes;
    }
    catch {
      return null;
    }
  }
}
